import React from "react";
import useStyles from "./style";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocalTaxiIcon from "@mui/icons-material/LocalTaxi";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import CorporateApi from "Services/Corporate";
import { CUSTOM_CONSTANTS } from "Constants";

const ApproveRideModal = (props) => {
  const { openDialog, rideData, handleClose } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();
  const dispatch = useDispatch();

  const handleApprove = () => {
    if (!rideData?._id) return;

    dispatch(showLoader("Approving ride and searching nearby drivers..."));

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Corporate ride approved successfully!",
            autoHideDuration: 4000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        handleClose(true);
      } else {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Approval completed with warning",
            autoHideDuration: 4000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "warning",
          })
        );
        handleClose(true);
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message:
            err?.response?.data?.message ||
            err?.response?.data?.data ||
            "Error approving corporate ride",
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    CorporateApi.approveCorporateRide(rideData._id).then(onSuccess, onFailure);
  };

  const passengerName =
    `${rideData?.userdetails?.firstName || ""} ${rideData?.userdetails?.lastName || ""}`.trim() ||
    rideData?.user_name ||
    "--";

  return (
    <Dialog
      fullScreen={fullScreen}
      open={Boolean(openDialog)}
      onClose={() => handleClose(false)}
      aria-labelledby="approve-corporate-ride-title"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle id="approve-corporate-ride-title" className={classes.dialogTitle}>
        <CheckCircleOutlineIcon sx={{ color: "#27ae60", fontSize: 28 }} />
        Approve Corporate Ride
      </DialogTitle>

      <DialogContent>
        <div className={classes.infoCard}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography className={classes.label}>Booking ID</Typography>
              <Typography className={classes.value}>
                {rideData?.bookingId || "--"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Status</Typography>
              <Box sx={{ mt: 0.5 }}>
                <span className={classes.badgeHold}>HOLD</span>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography className={classes.label}>Passenger</Typography>
              <Typography className={classes.value}>{passengerName}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.label}>Estimated Fare</Typography>
              <Typography className={classes.value}>
                {rideData?.estimatedFare ?? 0} {CUSTOM_CONSTANTS.BASE_CURRENCY}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.label}>Service Type</Typography>
              <Typography className={classes.value}>
                {rideData?.servicedetails?.serviceName || rideData?.service_name || "--"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.locationRow}>
                <div className={`${classes.locationDot} ${classes.pickupDot}`} />
                <Box>
                  <Typography className={classes.label}>Pickup Location</Typography>
                  <Typography variant="body2" sx={{ color: "#333", fontSize: "13px" }}>
                    {rideData?.pickupAddress || "--"}
                  </Typography>
                </Box>
              </div>

              <div className={classes.locationRow}>
                <div className={`${classes.locationDot} ${classes.dropDot}`} />
                <Box>
                  <Typography className={classes.label}>Drop Location</Typography>
                  <Typography variant="body2" sx={{ color: "#333", fontSize: "13px" }}>
                    {rideData?.dropAddress || "--"}
                  </Typography>
                </Box>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className={classes.noticeBox}>
          <strong>Note:</strong> Approving this ride will transition it to <strong>SEARCHING</strong> status and immediately search for and dispatch nearby available drivers.
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => handleClose(false)} className={classes.cancelBtn}>
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          variant="contained"
          className={classes.approveBtn}
          startIcon={<LocalTaxiIcon />}
        >
          Approve Ride
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveRideModal;
